import { useState } from "react";
import { ArrowLeft, CheckCircle, Loader2, PackageX, Tag } from "lucide-react";
import { ItemRow } from "./ItemRow";

const norm = (s) => (s || "").trim().toUpperCase();

export const PackPhase = ({ items, labels, packed, onTogglePacked, onImage, previews, loadPreview, onBack, onFinish, finishing }) => {
  const [expanded, setExpanded] = useState({});
  const toggleLabel = (itemId, labelId) => {
    const open = !expanded[itemId];
    setExpanded((prev) => ({ ...prev, [itemId]: open }));
    if (open && previews[labelId] === undefined) loadPreview(labelId);
  };
  const labelById = Object.fromEntries(labels.map((l) => [l.id, l]));
  const itemSkus = new Set(items.map((i) => norm(i.sku)).filter(Boolean));
  const linkedLabelIds = new Set(items.map((i) => i.pull_label_id).filter((id) => id && labelById[id]));

  const linkedLabel = (item) => (item.pull_label_id && labelById[item.pull_label_id]) || null;
  const labelFor = (item) => linkedLabel(item) || (norm(item.sku) && labels.find((l) => norm(l.sku_tag) === norm(item.sku))) || null;
  const hasLabel = (item) => !!labelFor(item);
  const toPack = items.filter(hasLabel);
  const setAside = items.filter((i) => !hasLabel(i));
  const extraLabels = labels.filter((l) => !linkedLabelIds.has(l.id) && (!norm(l.sku_tag) || !itemSkus.has(norm(l.sku_tag))));
  const packedCount = toPack.filter((i) => packed[i.id]).length;

  return (
    <div className="space-y-4" data-testid="pack-phase">
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-[#13132a]/95 backdrop-blur border-b border-white/[0.06]">
        <div className="flex items-baseline justify-between mb-1.5">
          <p className="text-base font-semibold text-white/90">Match, pack & apply label</p>
          <p className="text-sm font-bold text-[#00D4FF]" data-testid="pack-progress-text">{packedCount} of {toPack.length} packed</p>
        </div>
        <div className="h-2 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-[#00D4FF] transition-all duration-300" style={{ width: `${toPack.length ? Math.round((packedCount / toPack.length) * 100) : 0}%` }} />
        </div>
        <p className="text-xs text-white/40 mt-1.5">Tap the orange label button to drop down the label and check the customer name. Pack the item, stick the label on, then tap the row to check it off.</p>
      </div>

      {toPack.length === 0 ? (
        <p className="text-sm text-white/30 text-center py-6">None of the printed labels match an item on this list</p>
      ) : (
        <div className="space-y-2">
          {toPack.map((item) => {
            const linked = linkedLabel(item);
            const label = labelFor(item);
            const isOpen = !!expanded[item.id];
            return (
              <div key={item.id} data-testid={`pack-row-${item.id}`}>
                <ItemRow
                  item={item}
                  checked={!!packed[item.id]}
                  onToggle={() => onTogglePacked(item.id)}
                  onImage={onImage}
                  tag={linked ? `Label: ${linked.sku_tag || "linked"}` : item.pulled ? undefined : "Not pulled"}
                  tagColor={linked ? "#00D4FF" : "#F59E0B"}
                  labelAction={label ? { name: label.display_name || label.filename, open: isOpen, onClick: () => toggleLabel(item.id, label.id) } : null}
                  testId={`pack-item-${item.id}`}
                />
                {label && isOpen && (
                  <div className="mx-2 -mt-1 rounded-b-xl border border-t-0 border-[#FF6B35]/25 bg-[#FF6B35]/[0.04] p-3" data-testid={`pack-item-${item.id}-label-panel`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-white/60">
                        Label <span className="font-mono font-bold text-[#FF6B35]">{label.sku_tag || "No SKU"}</span> · <span className="text-white/80">{label.display_name || label.filename}</span>
                      </p>
                      <button onClick={() => toggleLabel(item.id, label.id)} className="text-white/40 text-xs" data-testid={`pack-item-${item.id}-label-collapse`}>Hide</button>
                    </div>
                    <div className="rounded-lg overflow-hidden bg-white">
                      {previews[label.id] ? (
                        <img src={previews[label.id]} alt="Shipping label" className="w-full max-h-[420px] object-contain" />
                      ) : previews[label.id] === null ? (
                        <p className="text-xs text-red-400 text-center py-6 bg-[#13132a]">Couldn't load this label</p>
                      ) : (
                        <div className="flex justify-center py-8 bg-[#13132a]"><Loader2 className="w-5 h-5 text-white/30 animate-spin" /></div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {setAside.length > 0 && (
        <div className="rounded-xl border border-[#F59E0B]/25 bg-[#F59E0B]/[0.04] p-3 space-y-2" data-testid="set-aside-section">
          <div className="flex items-center gap-2">
            <PackageX className="w-4 h-4 text-[#F59E0B]" />
            <p className="text-sm font-semibold text-[#F59E0B]">Set aside — no label yet ({setAside.length})</p>
          </div>
          <p className="text-xs text-white/40">These sold but don't have a printed label. Put them in the set-aside bin.</p>
          <div className="space-y-1.5">
            {setAside.map((item) => (
              <div key={item.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03]" data-testid={`set-aside-item-${item.id}`}>
                <span className={`text-base font-bold ${item.sku ? "text-[#FFB800]" : "text-white/30 text-xs"}`}>{item.sku || "No SKU"}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white/50 truncate">{item.title || "Untitled"}</p>
                  {item.pull_note && <p className="text-[11px] text-[#8B5CF6] truncate">{item.pull_note}</p>}
                  {!item.pulled && <span className="text-[10px] text-[#F59E0B]">not pulled</span>}
                </div>
                {item.pull_image_url && (
                  <img src={item.pull_image_url} alt="" className="w-9 h-9 rounded object-cover border border-white/10 cursor-pointer" onClick={() => onImage && onImage(item.pull_image_url)} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {extraLabels.length > 0 && (
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3 space-y-2" data-testid="extra-labels-section">
          <div className="flex items-center gap-2">
            <Tag className="w-4 h-4 text-white/50" />
            <p className="text-sm font-semibold text-white/70">Labels with no matching item ({extraLabels.length})</p>
          </div>
          <p className="text-xs text-white/40">Ask the admin to link these labels to their items.</p>
          <div className="space-y-1.5">
            {extraLabels.map((l) => (
              <div key={l.id} className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-white/[0.03]" data-testid={`extra-label-${l.id}`}>
                <span className="text-sm font-bold text-[#FF6B35] font-mono">{l.sku_tag || "No SKU"}</span>
                <span className="text-xs text-white/50 truncate flex-1">{l.display_name || l.filename}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0a0a14] via-[#0a0a14] to-transparent flex gap-2">
        <button onClick={onBack} className="px-4 py-4 rounded-xl bg-white/[0.06] border border-white/10 text-white/70" data-testid="pack-back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          onClick={onFinish}
          disabled={finishing}
          className="flex-1 flex items-center justify-center gap-2 py-4 rounded-xl bg-[#10B981] hover:bg-[#059669] text-white text-base font-semibold disabled:opacity-60"
          data-testid="complete-orders-btn"
        >
          {finishing ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
          {finishing ? "Finishing..." : "Finish Pull List"}
        </button>
      </div>
    </div>
  );
};
